import { AutoComplete, Button, Card, Col, Form, Input, Row, Select, Spin, Switch, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import TranslateFormWrapper from 'components/common/TranslationForm';
import { DEFAULT_VALUES } from 'config/constants';
import { uploadFile, removeFile } from 'services/storage.service';
import { useState, useCallback } from 'react';

import styles from '../ValueForm/index.module.scss';

interface AssetUploadFieldProps {
  value?: string;
  onChange?: (url: string) => void;
}

const AssetUploadField = ({ value, onChange }: AssetUploadFieldProps) => {
  const [loading, setLoading] = useState(false);

  const handleBeforeUpload = useCallback(async (file: File) => {
    setLoading(true);
    const previousUrl = value;
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('path', `product/assets/${file.name}-${crypto.randomUUID()}`);
      const { data } = await uploadFile(formData);
      onChange?.(data);
      // Best-effort: drop the file it replaces so uploads don't pile up as orphans.
      // Never blocks the swap itself — a failed cleanup just leaves the old file behind.
      if (previousUrl) removeFile(previousUrl).catch(() => undefined);
    } catch (e: any) {
      message.error(e?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
    return false;
  }, [onChange, value]);

  return (
    <Upload showUploadList={false} beforeUpload={handleBeforeUpload} accept='image/*,video/*'>
      {value ? (
        <div style={{ cursor: 'pointer' }}>
          <img src={value} alt='asset' style={{ width: 80, height: 54, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
          <span style={{ fontSize: 11, color: '#1890ff' }}>Click to change</span>
        </div>
      ) : (
        <Button icon={loading ? <Spin size='small' /> : <UploadOutlined />} loading={loading} size='small'>
          Upload file
        </Button>
      )}
    </Upload>
  );
};

export interface ValueFormItemProps {
  category: Category,
  field: any
}

const ValueFormItem = ({ field, category }: ValueFormItemProps) => (
  <Row key={field.key}>
    <Col span={12}>
      <Card className={styles.valueContainer} title='Product value'>
        <Form.Item
          label='Link'
          name={[field.name, 'link']}
        >
          <Input placeholder='Link' />
        </Form.Item>
        <Form.Item
          label='Origin price'
          name={[field.name, 'originPrice']}
        >
          <AutoComplete
            placeholder='Original price'
            options={DEFAULT_VALUES}
          />
        </Form.Item>
        <Form.Item
          label='Purchase price'
          name={[field.name, 'purchasePrice']}
        >
          <Input placeholder='Purchase price' />
        </Form.Item>
        <Form.Item
          label='Price'
          name={[field.name, 'price']}
        >
          <Input placeholder='Price' />
        </Form.Item>
        <Form.Item
          label='Display'
          name={[field.name, 'display']}
        >
          <Input placeholder='Display' />
        </Form.Item>
        <Form.Item
          label='Discount'
          name={[field.name, 'discount']}
        >
          <Input placeholder='Discount' />
        </Form.Item>
        <Form.Item
          label='Age'
          name={[field.name, 'age']}
        >
          <Input placeholder='e.g. 3+' />
        </Form.Item>
        <Form.Item
          label='Description'
          name={[field.name, 'valueDescription']}
        >
          <Input.TextArea placeholder='description' />
        </Form.Item>
        <Form.Item
          label='Visible'
          valuePropName='checked'
          name={[field.name, 'visible']}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label='Out of stock'
          valuePropName='checked'
          name={[field.name, 'outOfStock']}
        >
          <Switch />
        </Form.Item>
        <TranslateFormWrapper
          field={field}
          items={[
            {
              name: 'name',
              item: <Input placeholder='name translation' />,
            },
            {
              name: 'description',
              item: <Input.TextArea placeholder='description translation' />,
            },
          ]}
        />
        <Form.List name={[field.name, 'characteristics']}>
          {(charFields, { add: addChar, remove: removeChar }) => (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>Characteristics</div>
              {charFields.map((charField) => (
                <Card
                  key={charField.key}
                  size='small'
                  style={{ marginBottom: 8 }}
                  extra={<Button danger size='small' onClick={() => removeChar(charField.name)}>✕</Button>}
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item label='Назва UA' name={[charField.name, 'name', 'ua']} style={{ marginBottom: 4 }}>
                        <Input placeholder='напр. Довжина' />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label='Название RU' name={[charField.name, 'name', 'ru']} style={{ marginBottom: 4 }}>
                        <Input placeholder='напр. Длина' />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label='Значення UA' name={[charField.name, 'value', 'ua']} style={{ marginBottom: 0 }}>
                        <Input placeholder='напр. 15 см' />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label='Значение RU' name={[charField.name, 'value', 'ru']} style={{ marginBottom: 0 }}>
                        <Input placeholder='напр. 15 см' />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Button size='small' onClick={() => addChar({ name: { ua: '', ru: '' }, value: { ua: '', ru: '' } })}>
                + Add characteristic
              </Button>
            </div>
          )}
        </Form.List>

        <Form.List name={[field.name, 'assets']}>
          {(assetFields, { add: addAsset, remove: removeAsset }) => (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>Assets</div>
              {assetFields.map((assetField) => (
                <Row key={assetField.key} gutter={8} style={{ marginBottom: 10 }} align='middle'>
                  <Col>
                    <Form.Item name={[assetField.name, 'src']} noStyle>
                      <AssetUploadField />
                    </Form.Item>
                  </Col>
                  <Col style={{ width: 110 }}>
                    <Form.Item name={[assetField.name, 'type']} noStyle>
                      <Select
                        placeholder='Type'
                        options={[
                          { label: 'Image', value: 'img' },
                          { label: 'Video', value: 'video' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button danger size='small' onClick={() => removeAsset(assetField.name)}>✕</Button>
                  </Col>
                </Row>
              ))}
              <Button size='small' onClick={() => addAsset({ src: '', type: 'img' })}>+ Add asset</Button>
            </div>
          )}
        </Form.List>
      </Card>
    </Col>
    <Col span={12}>
      <Card title='Product SEO value'>
        <Form.Item
          label='Meta title'
          name={[field.name, 'seoTitle']}
        >
          <Input placeholder='Meta title' />
        </Form.Item>
        <Form.Item
          label='Description'
          name={[field.name, 'seoDescription']}
        >
          <Input.TextArea placeholder='Meta description' />
        </Form.Item>
        <Form.Item
          label='Keywords'
          name={[field.name, 'keywords']}
        >
          <Input.TextArea placeholder='Keywords' />
        </Form.Item>
        <TranslateFormWrapper
          field={field}
          prefix='seo'
          items={[
            {
              name: 'seoTitle',
              label: 'Title',
              item: <Input placeholder='Seo title translation' />,
            },
            {
              name: 'seoDescription',
              label: 'Description',
              item: <Input.TextArea placeholder='Description' />,
            },
            {
              name: 'seoKeywords',
              label: 'Keywords',
              item: <Input.TextArea placeholder='Keywords' />,
            },
          ]}
        />
      </Card>
    </Col>
  </Row>
);

export default ValueFormItem;
